import { PrevNext } from '@/components/CommonComponents/PrevNext';
import { Stepper } from '@/components/CommonComponents/Stepper';
import SuccessModal from '@/components/CommonComponents/SuccessModal';
import PageHeaderWrapper from '@/components/Layout/PageHeaderWrapper';
import { showToast } from '@/helper/toast.helper';
import { formatDateForInput } from '@/helper/utils';
import { StepValidatorRef } from '@/hooks/useStepValidation';
import { AppDispatch, RootState } from '@/store';
import { fetchMetaData } from '@/store/slices/metaDataSlice/metaDataSlice';
import {
  clearProjectsDropdown,
  getProjectsDropdown,
} from '@/store/slices/projectsSlice/projectsSlice';
import { getSitesByProjectId } from '@/store/slices/siteManagementSlice/siteManagementSlice';
import {
  clearSurveyListBasedOnLanguage,
  getAllSurveyListBasedOnLanguages,
} from '@/store/slices/surveyManagementSlice/surveyManagementSlice';
import {
  clearSelectedSurveySet,
  createSurveySet,
  getSurveySetById,
  updateSurveySet,
} from '@/store/slices/surveySetManagementSlice/surveySetManagementSlice';
import { ClaimType, CreateSurveySetDto, SubmissionRule } from '@/types/surveySet.types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import AddSurveySet from './AddSurveySet';
import BasicInfo from './BasicInfo';
import NotificationSettings from './NotificationSettings';
import ReviewAndCreate from './ReviewAndCreate';
// import BasicInfo from './BasicInfo';

type FromSource = 'dashboard' | 'surveyset-management';
type NavState = {
  from?: FromSource;
  surveySetId?: string;
};

const getQueryFrom = (search: string): FromSource | undefined => {
  const params = new URLSearchParams(search);
  const from = params.get('from');
  return from === 'dashboard' || from === 'surveyset-management' ? from : undefined;
};

export interface surveysetCreationType {
  // Basic Info
  title: string;
  numberOfSurveys: number;
  siteId: number;
  projectId: number;
  currency: string;
  rewardAmount: number;
  claimType: ClaimType;
  startDate: string;
  endDate: string;
  timezone: string;

  languages: number[];

  // Participant Limits
  participantLimit: boolean | null;
  maxParticipants?: number;

  // Messages
  greetingMsg?: string;
  consentMsg?: string;
  rewardMsg?: string;

  // Submission
  submissionRule: SubmissionRule | null;
  status: boolean;

  // Surveys
  surveyIds: number[];

  // flag fields
  userModifiedCurrency?: boolean;
  userModifiedTimezone?: boolean;
}

const DEFAULT_FORM_VALUES: surveysetCreationType = {
  title: '',
  numberOfSurveys: 1,
  siteId: 0,
  projectId: 0,
  currency: '',
  rewardAmount: 0,
  claimType: ClaimType.PER_SURVEY,
  startDate: '',
  endDate: '',
  timezone: '',
  languages: [],

  participantLimit: null,
  maxParticipants: 0,

  greetingMsg: 'Welcome to our Employee Satisfaction Survey. Your feedback is valuable to us.',
  consentMsg:
    'By participating in this survey you consent to the collection and analysis of your response.',
  rewardMsg: 'Thank you for completing the survey. Your reward will be processed shortly.',

  submissionRule: null,
  status: true,

  surveyIds: [0], // MUST MATCH numberOfSurveys
};

const CreateSurveySet: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  const methods = useForm<surveysetCreationType>({
    mode: 'onChange',
    shouldUnregister: false,
    defaultValues: {
      ...DEFAULT_FORM_VALUES,
      projectId: (location.state as any)?.fromProject?.id ?? 0,
    },
  });

  const { reset, getValues, watch } = methods;
  const projectId = watch('projectId');

  const stepRefs = useRef<(StepValidatorRef | null)[]>([]);
  const initialEditValuesRef = useRef<surveysetCreationType | null>(null);
  const prevLanguagesRef = useRef<number[] | null>(null);

  const [step, setStep] = useState<number>(0);
  const [isConfirmed, setIsConfirmed] = useState<boolean>(false);
  const [showPopup, setShowPopup] = useState(false);
  const [createdSurveySet, setCreatedSurveySet] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { list: projectsList, loading: projectsLoading } = useSelector(
    (state: RootState) => state.projects,
  );
  const { sitesByProject, loadingSitesByProject } = useSelector((state: RootState) => state.sites);
  const {
    list: surveysList,
    listbasedOnLanguage: surveyListbasedOnLanguage,
    loading: surveysLoading,
  } = useSelector((state: RootState) => state.survey);
  const { selected: selectedSurveySet, loading: surveySetLoading } = useSelector(
    (state: RootState) => state.surveySet,
  );
  const { loading, languages: languagesList } = useSelector((state: RootState) => state.metaData);

  const from = useMemo<FromSource | undefined>(() => {
    const state = (location.state as NavState | null) ?? {};
    return state.from ?? getQueryFrom(location.search);
  }, [location.state, location.search]);

  const fromProject = (location.state as any)?.fromProject as
    | { id: number; name: string }
    | undefined;

  const surveySetId = (location.state as NavState)?.surveySetId;
  const isEditing = !!surveySetId;

  const projectName = useMemo(() => {
    if (!projectId) return '-';
    return projectsList.projectsDropdown?.find((p) => p.id === projectId)?.name || '-';
  }, [projectId, projectsList.projectsDropdown]);

  const languageOptions = useMemo(() => {
    if (!sitesByProject || sitesByProject.length === 0) return [];

    const map = new Map<number, any>();

    sitesByProject.forEach((site) => {
      site.languages?.forEach((lang) => {
        map.set(lang.id, lang);
      });
    });

    return Array.from(map.values());
  }, [sitesByProject]);

  // }, [projectsList.data, selectedProjectId])

  useEffect(() => {
    dispatch(clearProjectsDropdown());
    dispatch(getProjectsDropdown());
    dispatch(fetchMetaData());
    if (fromProject?.id) {
      dispatch(getSitesByProjectId(fromProject.id));
    }

    return () => {
      dispatch(clearSelectedSurveySet());
    };
  }, [dispatch]);

  const selectedLanguages = watch('languages');

  useEffect(() => {
    if (!selectedLanguages || selectedLanguages.length === 0) {
      prevLanguagesRef.current = null;
      dispatch(clearSurveyListBasedOnLanguage());
      return;
    }

    //prevent duplicate calls
    if (
      prevLanguagesRef.current &&
      JSON.stringify(prevLanguagesRef.current) === JSON.stringify(selectedLanguages)
    ) {
      return;
    }

    prevLanguagesRef.current = selectedLanguages;

    dispatch(getAllSurveyListBasedOnLanguages({ languageIds: selectedLanguages }));
  }, [selectedLanguages, dispatch]);

  useEffect(() => {
    if (!isEditing || !surveySetId) return;

    const loadSurveySet = async () => {
      const surveySet = await dispatch(getSurveySetById(surveySetId)).unwrap();

      // Fetch sites for the selected project first
      if (surveySet.site.project.id) {
        await dispatch(getSitesByProjectId(surveySet.site.project.id));
      }

      const formValues: surveysetCreationType = {
        title: surveySet.title,
        numberOfSurveys: surveySet.numberOfSurveys,
        projectId: surveySet.site.project.id,
        siteId: surveySet.site.id,
        currency: surveySet.currency,
        rewardAmount: surveySet.rewardAmount,
        claimType: surveySet.claimType,
        startDate: formatDateForInput(surveySet.startDate!),
        endDate: formatDateForInput(surveySet.endDate!),
        timezone: surveySet?.timezone,
        languages: surveySet.languages?.length
          ? surveySet.languages.map((l) => l.id)
          : surveySet.site.languages.map((l) => l.id),
        participantLimit: surveySet.participantLimit!,
        maxParticipants: surveySet.maxParticipants,
        greetingMsg: surveySet.greetingMsg,
        consentMsg: surveySet.consentMsg,
        rewardMsg: surveySet.rewardMsg,
        submissionRule: surveySet.submissionRule,
        status: surveySet.status,
        surveyIds: surveySet.surveys?.map((s) => s.survey.id),
      };

      initialEditValuesRef.current = formValues;
      reset(formValues);
    };

    loadSurveySet();
  }, [isEditing, surveySetId, dispatch, reset]);

  const handlePrev = () => setStep((prev) => Math.max(prev - 1, 0));
  const handleNext = async () => {
    const validator = stepRefs.current[step];
    if (validator) {
      const valid = await validator.validate();
      if (!valid) return;
    }

    setStep((prev) => Math.min(prev + 1, 3));
  };

  const handleReset = () => {
    const currentValues = methods.getValues();

    // helper to rebuild surveyIds correctly
    const buildSurveyIds = (count: number) => Array.from({ length: count }, () => 0);

    // ================= EDIT MODE =================
    if (isEditing && initialEditValuesRef.current) {
      const initial = initialEditValuesRef.current;

      methods.reset({
        ...initial,
        numberOfSurveys: initial.surveyIds?.length ?? initial.numberOfSurveys,
        surveyIds: initial.surveyIds ?? [],
      });

      return;
    }

    // ================= CREATE MODE =================
    switch (step) {
      case 0: {
        methods.reset({
          ...currentValues,
          title: '',
          numberOfSurveys: 1,
          surveyIds: [0], // 🔑 keep in sync
          languages: [],
          siteId: 0,
          projectId: fromProject?.id ?? 0,
          currency: '',
          rewardAmount: 0,
          claimType: ClaimType.COMPLETE_SURVEY_SET,
          startDate: '',
          endDate: '',
          timezone: '',
          status: true,
        });
        break;
      }

      case 1: {
        const count = currentValues.numberOfSurveys;

        methods.reset({
          ...currentValues,
          surveyIds: buildSurveyIds(count),
        });
        break;
      }

      case 2: {
        methods.reset({
          ...currentValues,
          submissionRule: null,
          participantLimit: null,
          maxParticipants: undefined,
        });
        break;
      }

      case 3: {
        setIsConfirmed(false);
        break;
      }
    }
  };

  const stepperSteps = useMemo(
    () => [
      {
        title: 'Basic Information',
        subtitle: 'Enter basic survey-set details',
      },
      {
        title: 'Add Surveys to Survey-Set',
        subtitle: 'Surveys for individual days',
      },
      {
        title: 'Notification Settings',
        subtitle: 'consent message and users',
      },
      {
        title: isEditing ? 'Review & Update' : 'Review & Create',
        subtitle: 'Final check & confirmation',
      },
    ],
    [isEditing],
  );

  const handleCreateOrUpdate = async () => {
    setIsSubmitting(true);
    try {
      const values = getValues();
      const payload: CreateSurveySetDto = {
        title: values.title,
        numberOfSurveys: values.numberOfSurveys,
        siteId: values.siteId,
        participantLimit: values.participantLimit!,
        maxParticipants: values.maxParticipants,
        currency: values.currency,
        claimType: values.claimType,
        rewardAmount: values.rewardAmount,
        submissionRule: values.submissionRule!,
        status: values.status,
        startDate: values.startDate,
        endDate: values.endDate,
        timezone: values.timezone,
        greetingMsg: values.greetingMsg,
        consentMsg: values.consentMsg,
        rewardMsg: values.rewardMsg,
        survey: values.surveyIds.slice(0, values.numberOfSurveys),
        languages: values.languages,
      };

      let result;
      if (isEditing && surveySetId) {
        result = await dispatch(
          updateSurveySet({
            id: surveySetId,
            data: payload,
          }),
        ).unwrap();
        setCreatedSurveySet(result);
      } else {
        result = await dispatch(createSurveySet(payload)).unwrap();
        setCreatedSurveySet(result.data);
      }

      setShowPopup(true);
    } catch (error) {
      console.error('Error creating/updating survey set:', error);
      showToast.error((error as string) || 'Error creating/updating survey set:');
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalDataFields = createdSurveySet
    ? [
        { label: 'Survey-set ID', value: String(createdSurveySet?.id || '-') },
        { label: 'Poster Code', value: createdSurveySet?.posterCode || '-' },
        { label: 'Title', value: createdSurveySet?.title || '-' },
        { label: 'Project', value: projectName },
        {
          label: 'Location / Site',
          value: sitesByProject?.find((site) => site.id === createdSurveySet?.siteId)?.name || '-',
        },
      ]
    : [];

  const modalActions = ['Role-based access granted', 'Audit log entry recorded'];

  return (
    <PageHeaderWrapper
      title="Survey-Set Management"
      description="Manage and monitor surveys across all projects"
      onAdd={() => navigate(-1)}
      addLabel=" Return to Survey-Set List"
      exportLabel="Export"
    >
      {/* Stepper */}
      <Stepper steps={stepperSteps} activeStep={step} />

      {/* Step Content */}
      <div className="bg-white rounded-xl">
        <FormProvider {...methods}>
          {step === 0 && (
            <BasicInfo
              ref={(el) => {
                stepRefs.current[0] = el;
              }}
              isEditing={isEditing}
              projects={projectsList.projectsDropdown || []}
              loadingSites={loadingSitesByProject}
              sites={sitesByProject}
              languages={languageOptions || []}
              lockedProjectId={fromProject?.id}
            />
          )}
          {step === 1 && (
            <AddSurveySet
              isEditing={isEditing}
              surveys={surveyListbasedOnLanguage.data}
              ref={(el) => {
                stepRefs.current[1] = el;
              }}
            />
          )}
          {step === 2 && (
            <NotificationSettings
              isEditing={isEditing}
              ref={(el) => {
                stepRefs.current[2] = el;
              }}
            />
          )}
          {step === 3 && (
            <ReviewAndCreate
              projects={projectsList.projectsDropdown || []}
              sites={sitesByProject}
              surveys={surveyListbasedOnLanguage.data}
              isConfirmed={isConfirmed}
              setIsConfirmed={setIsConfirmed}
              showSuccessModal={showPopup}
              isEditing={isEditing}
            />
          )}
        </FormProvider>

        {/* Navigation Buttons */}
        <div className="px-6 pb-6 pt-2">
          <PrevNext
            hidePrevious={step === 0}
            prev={handlePrev}
            next={handleNext}
            onReset={handleReset}
            create={step === 3}
            createLabel={isEditing ? 'Update Survey-Set →' : 'Create New Survey-Set →'}
            onCreate={handleCreateOrUpdate}
            disableCreate={!isConfirmed || isSubmitting}
            disableNext={isSubmitting}
            disableReset={step === 3}
            isSubmitting={isSubmitting}
          />
        </div>
      </div>

      {/* Success Popup (inline) */}
      <SuccessModal
        isOpen={showPopup}
        actionsHeader="Post-Creation Actions:"
        title={isEditing ? 'Survey-Set Updated Successfully!' : 'Survey-Set Created Successfully!'}
        dataFields={modalDataFields}
        actions={modalActions}
        onClose={() => setShowPopup(false)}
        onPrimaryAction={() => {
          // clear edit memory
          initialEditValuesRef.current = null;

          // reset to true defaults
          methods.reset({
            ...DEFAULT_FORM_VALUES,
          });

          setShowPopup(false);
          setStep(0);
          setIsConfirmed(false);
        }}
        onSecondaryAction={() => {
          setShowPopup(false);
          navigate('/dashboard/surveyset-management');
        }}
        primaryButtonText="Create Another Survey-Set"
        secondaryButtonText="View Survey-Set List"
        hidePrimaryButton={isEditing}
      />
    </PageHeaderWrapper>
  );
};

export default CreateSurveySet;
